import { useState, useCallback, useEffect } from 'react';
import { Card, CardHeader, Button, Input, Select, LoadingSkeleton, Badge } from '@nexora/design-system';
import {
  useNumberSequences,
  useCreateNumberSequence,
  useGenerateNumber,
  type NumberSequenceInput,
} from './useOrganization';

const SEQUENCE_TYPE_OPTIONS = [
  { value: 'invoice', label: 'Fatura (NF-e)' },
  { value: 'nfse', label: 'NFS-e' },
  { value: 'nfce', label: 'NFC-e' },
  { value: 'order', label: 'Pedido' },
  { value: 'quote', label: 'Cotação' },
  { value: 'contract', label: 'Contrato' },
  { value: 'proposal', label: 'Proposta' },
  { value: 'service-order', label: 'Ordem de Serviço' },
];

export function NumberSequencesPage(): JSX.Element {
  const [companyId, setCompanyId] = useState('');
  const [companyInput, setCompanyInput] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [generateResult, setGenerateResult] = useState<string | null>(null);
  const [generatingId, setGeneratingId] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastVariant, setToastVariant] = useState<'success' | 'error'>('success');

  const { data: sequences, isLoading, error } = useNumberSequences(companyId);
  const createMutation = useCreateNumberSequence();
  const generateMutation = useGenerateNumber();

  useEffect(() => {
    if (toastMessage) {
      const timer = setTimeout(() => setToastMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [toastMessage]);

  const showToast = useCallback((message: string, variant: 'success' | 'error') => {
    setToastMessage(message);
    setToastVariant(variant);
  }, []);

  const handleLoad = useCallback(() => {
    setCompanyId(companyInput.trim());
  }, [companyInput]);

  const handleCreate = useCallback(
    async (input: NumberSequenceInput) => {
      try {
        await createMutation.mutateAsync(input);
        showToast('Sequencia criada com sucesso.', 'success');
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Erro ao criar sequencia.';
        showToast(msg, 'error');
        throw err;
      }
    },
    [createMutation, showToast],
  );

  const handleGenerate = useCallback(
    async (id: string) => {
      setGeneratingId(id);
      try {
        const result = await generateMutation.mutateAsync(id);
        setGenerateResult(result.number);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Erro ao gerar número.';
        showToast(msg, 'error');
      } finally {
        setGeneratingId(null);
      }
    },
    [generateMutation, showToast],
  );

  return (
    <div style={{ padding: '1.5rem' }}>
      {/* Page header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '1.5rem',
        }}
      >
        <h1 className="font-display-lg text-display-lg text-on-surface m-0">
          Sequencias Numericas
          {sequences && (
            <span className="font-body-md text-body-md text-on-surface-variant font-normal ml-sm">
              ({sequences.length})
            </span>
          )}
        </h1>
        {companyId && (
          <Button variant="primary" onClick={() => setModalOpen(true)}>
            + Nova Sequencia
          </Button>
        )}
      </div>

      {/* Company ID filter */}
      <Card padding="1rem" variant="outlined" className="filter-card">
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.75rem', flexWrap: 'wrap' }}>
          <div style={{ minWidth: '300px', flex: 1 }}>
            <Input
              label="ID da Empresa"
              value={companyInput}
              onChange={(e) => setCompanyInput(e.target.value)}
              placeholder="Ex: uuid-da-empresa"
            />
          </div>
          <Button variant="primary" onClick={handleLoad} disabled={!companyInput.trim()}>
            Carregar
          </Button>
        </div>
      </Card>

      {/* Content */}
      {companyId && (
        <Card variant="outlined" padding="0">
          <CardHeader
            className="px-md pt-md"
            action={
              <span className="font-label-md text-label-md text-on-surface-variant tabular-nums">
                {sequences?.length ?? 0} {(sequences?.length ?? 0) === 1 ? 'sequência' : 'sequências'}
              </span>
            }
          >
            <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Sequências numéricas</h3>
          </CardHeader>
          {/* Loading */}
          {isLoading && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '3rem',
                gap: '0.75rem',
                color: '#43474e',
              }}
            >
              <LoadingSkeleton />
              <span style={{ fontSize: '0.875rem' }}>Carregando sequencias...</span>
            </div>
          )}

          {/* Error */}
          {error && !isLoading && (
            <div
              style={{
                padding: '3rem 2rem',
                textAlign: 'center',
                color: '#ba1a1a',
              }}
            >
              <p style={{ fontWeight: 600, margin: '0 0 0.5rem' }}>Erro ao carregar sequencias</p>
              <p style={{ fontSize: '0.875rem', margin: 0, color: '#74777f' }}>
                {error instanceof Error ? error.message : 'Erro desconhecido'}
              </p>
            </div>
          )}

          {/* Empty */}
          {!isLoading && !error && sequences && sequences.length === 0 && (
            <div
              style={{
                padding: '3rem 2rem',
                textAlign: 'center',
                color: '#74777f',
              }}
            >
              <p style={{ margin: '0 0 1rem', fontSize: '0.875rem' }}>
                Nenhuma sequencia numerica encontrada para esta empresa.
              </p>
              <Button variant="outline" size="sm" onClick={() => setModalOpen(true)}>
                Criar primeira sequencia
              </Button>
            </div>
          )}

          {/* Table */}
          {!isLoading && !error && sequences && sequences.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '0.875rem',
                }}
              >
                <thead>
                  <tr>
                    {['Tipo', 'Padrão', 'Último Número', 'Criada em', 'Ações'].map((h) => (
                      <th
                        key={h}
                        style={{
                          textAlign: 'left',
                          padding: '0.75rem 1rem',
                          background: '#f2f4f6',
                          color: '#43474e',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          borderBottom: '2px solid #e0e3e5',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sequences.map((seq) => (
                    <tr
                      key={seq.id}
                      style={{ transition: 'background 0.15s ease' }}
                      className="org-table-row"
                    >
                      <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0' }}>
                        <Badge variant="info" size="sm">{seq.entity}</Badge>
                      </td>
                      <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0', fontFamily: 'monospace' }}>
                        {seq.format}
                      </td>
                      <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0', fontWeight: 600 }}>
                        {seq.currentSequence}
                      </td>
                      <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0', whiteSpace: 'nowrap' }}>
                        {new Date(seq.createdAt).toLocaleDateString('pt-BR')}
                      </td>
                      <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0', whiteSpace: 'nowrap' }}>
                        <Button
                          variant="primary"
                          size="sm"
                          loading={generatingId === seq.id}
                          onClick={() => handleGenerate(seq.id)}
                        >
                          Gerar
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Company not selected */}
      {!companyId && (
        <Card padding="2rem" variant="outlined">
          <p style={{ textAlign: 'center', color: '#74777f', fontSize: '0.875rem', margin: 0 }}>
            Informe o ID da empresa acima para carregar as sequencias numericas.
          </p>
        </Card>
      )}

      {/* Create form modal */}
      <SequenceFormModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        companyId={companyId}
        onSubmit={handleCreate}
      />

      {/* Generate result modal */}
      {generateResult && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setGenerateResult(null); }}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '12px',
              padding: '2rem',
              width: '100%',
              maxWidth: '400px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
              textAlign: 'center',
            }}
          >
            <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', fontWeight: 700, color: '#191c1e' }}>
              Número Gerado
            </h3>
            <p
              style={{
                fontSize: '1.75rem',
                fontWeight: 700,
                color: '#002045',
                fontFamily: 'monospace',
                margin: '1rem 0',
                letterSpacing: '0.05em',
              }}
            >
              {generateResult}
            </p>
            <Button variant="primary" onClick={() => setGenerateResult(null)}>
              Fechar
            </Button>
          </div>
        </div>
      )}

      {/* Toast */}
      {toastMessage && (
        <div
          role="alert"
          style={{
            position: 'fixed',
            bottom: '1.5rem',
            right: '1.5rem',
            padding: '0.75rem 1rem',
            borderRadius: '8px',
            background: toastVariant === 'success' ? '#d3f5d6' : '#ffdad6',
            border: `1px solid ${toastVariant === 'success' ? '#b6ebbb' : '#ffb4ab'}`,
            color: toastVariant === 'success' ? '#2e7d32' : '#ba1a1a',
            fontSize: '0.875rem',
            fontWeight: 500,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            zIndex: 10000,
            maxWidth: '400px',
            animation: 'toast-slide-in 0.3s ease',
          }}
        >
          {toastMessage}
          <button
            onClick={() => setToastMessage(null)}
            aria-label="Fechar"
            style={{
              marginLeft: '0.75rem',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'inherit',
              fontSize: '1rem',
              padding: '0',
              lineHeight: 1,
            }}
          >
            &times;
          </button>
        </div>
      )}

      <style>{`
        .org-table-row:hover td {
          background: #eceef0;
        }
        .filter-card { margin-bottom: 1rem; }
        @keyframes toast-slide-in {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

/* ── Sequence Form Modal ── */

interface SequenceFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
  onSubmit: (data: NumberSequenceInput) => Promise<void>;
}

function SequenceFormModal({ isOpen, onClose, companyId, onSubmit }: SequenceFormModalProps): JSX.Element {
  const [type, setType] = useState('invoice');
  const [pattern, setPattern] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setType('invoice');
      setPattern('');
      setError(null);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);

    if (!pattern.trim()) {
      setError('Padrão da sequencia e obrigatório.');
      return;
    }

    try {
      setSubmitting(true);
      await onSubmit({ companyId, entity: type, format: pattern.trim() });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar sequencia.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: isOpen ? 'flex' : 'none',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          background: '#ffffff',
          borderRadius: '12px',
          padding: '1.5rem',
          width: '100%',
          maxWidth: '480px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
        }}
      >
        <h2 style={{ margin: '0 0 1.25rem', fontSize: '1.125rem', fontWeight: 700, color: '#191c1e' }}>
          Nova Sequencia Numerica
        </h2>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <Select
              label="Tipo"
              value={type}
              onChange={(e) => setType(e.target.value)}
            >
              {SEQUENCE_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>

            <Input
              label="Padrão"
              value={pattern}
              onChange={(e) => setPattern(e.target.value)}
              placeholder='Ex: NF-%Y%m####'
              required
            />

            {error && (
              <p role="alert" style={{ margin: 0, fontSize: '0.8125rem', color: '#ba1a1a' }}>
                {error}
              </p>
            )}
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: '0.75rem',
              marginTop: '1.5rem',
              paddingTop: '1rem',
              borderTop: '1px solid #e0e3e5',
            }}
          >
            <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" loading={submitting}>
              Salvar
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
